import { type RefObject } from 'react';

import { useReplacementEngine } from './useReplacementEngine';
import { useReplacementRenderer } from './useReplacementRenderer';

interface UseHtmlContentProcessorProps {
  containerRef: RefObject<HTMLElement>;
  content: string;
  displayThirdPartyConsent?: boolean;
  enableImagePreview?: boolean;
}

interface UseHtmlContentProcessorReturn {
  dangerouslySetInnerHTML: { __html: string };
  portals: Array<{
    element: JSX.Element;
    key: string;
    target: Element;
  }>;
}

/**
 * High-level hook that combines replacement engine and renderer
 *
 * This hook processes HTML content through the replacement engine
 * and renders the results as React portals, providing a simplified
 * API for the HtmlContent component.
 *
 * @param content - HTML content to process
 * @param containerRef - Reference to the container element
 * @param displayThirdPartyConsent - Whether to process iframes with consent
 * @param enableImagePreview - Whether to process images with preview
 * @returns Object containing processed HTML and portal elements
 */
export const useHtmlContentProcessor = ({
  content,
  containerRef,
  displayThirdPartyConsent = false,
  enableImagePreview = false,
}: UseHtmlContentProcessorProps): UseHtmlContentProcessorReturn => {
  const { dangerouslySetInnerHTML, replacements } = useReplacementEngine({
    content,
    displayThirdPartyConsent,
    enableImagePreview,
  });

  const portals = useReplacementRenderer({
    replacements,
    containerRef,
    enableImagePreview,
  });

  return {
    dangerouslySetInnerHTML,
    portals,
  };
};
